import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bug, X, Database, Loader2, CheckCircle2, AlertTriangle, Trash2, Smartphone, Shield, UserPlus } from 'lucide-react';
import { api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

const DebugMenu: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  
  const [isVisible, setIsVisible] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [apiStatus, setApiStatus] = useState<'checking' | 'ok' | 'error' | 'unauthorized'>('checking');
  
  // 生成確認用ステート
  const [isConfirmingGenerate, setIsConfirmingGenerate] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // 削除確認用ステート
  const [isConfirmingClear, setIsConfirmingClear] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  
  const linkClass = "block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-blue-600 transition-colors cursor-pointer font-bold";
  
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
        const target = e.target as HTMLElement;
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable) {
            return;
        }
        if (e.key === '#' || (e.shiftKey && e.key === '3')) {
            setIsVisible(prev => !prev);
        }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const checkApiConnection = async () => {
      setApiStatus('checking');
      if (!currentUser) {
          setApiStatus('unauthorized');
          return;
      }
      try {
          await api.getStores();
          setApiStatus('ok');
      } catch (e) {
          console.error("Debug API Check Error:", e);
          setApiStatus('error');
      }
  };

  const handleGenerateData = async () => {
      setIsGenerating(true);
      setIsConfirmingGenerate(false);
      try {
          await api.generateDemoData();
          showToast('デモデータを生成しました。', 'success');
          setIsOpen(false);
          navigate('/admin/shifts');
          setTimeout(() => {
              window.location.hash = '#/admin/shifts';
          }, 1000);
      } catch(e: unknown) {
          console.error(e);
          const message = e instanceof Error ? e.message : '';
          const errorMsg = message.includes('quota')
            ? 'APIの利用制限（クォータ）を超過しました。しばらく待ってから再試行してください。'
            : '生成に失敗しました。';
          showToast(errorMsg, 'error');
      } finally {
          setIsGenerating(false);
      }
  };

  const handleClearData = async () => {
      setIsClearing(true);
      setIsConfirmingClear(false);
      try {
          await api.clearAllData();
          showToast('全データを初期化しました。', 'success');
          setIsOpen(false);
          // 状態を完全にリセットするためセットアップ画面へ
          navigate('/admin/setup');
      } catch (e: unknown) {
          console.error(e);
          showToast('初期化に失敗しました。', 'error');
      } finally {
          setIsClearing(false);
      }
  };

  useEffect(() => {
      if (isVisible) {
          checkApiConnection();
      }
  }, [isVisible, currentUser]);

  if (!isVisible) return null;

  const handleNavigate = (path: string) => {
      setIsOpen(false);
      const isProtectedRoute = path.startsWith('/system') || path.startsWith('/admin') || path.startsWith('/user');
      if (isProtectedRoute && !currentUser && path !== '/invite') {
          showToast('このページにはログインが必要です', 'info');
          navigate('/login');
          return;
      }
      navigate(path);
  };

  return (
    <div className="fixed bottom-4 right-4 z-[9999] font-sans">
      {!isOpen && (
        <button 
          onClick={() => setIsOpen(true)}
          className="bg-slate-800 text-white p-3 rounded-full shadow-lg hover:bg-slate-700 transition-transform hover:scale-110 flex items-center justify-center border-2 border-slate-600 cursor-pointer group"
        >
          <Bug size={24} />
          <span className={`absolute top-0 right-0 w-3 h-3 rounded-full border-2 border-slate-800 ${
              apiStatus === 'ok' ? 'bg-green-500' : 
              apiStatus === 'error' ? 'bg-red-500' : 
              apiStatus === 'unauthorized' ? 'bg-orange-500' :
              'bg-gray-400'
          }`}></span>
          <span className="absolute right-full mr-2 bg-slate-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none transition-opacity">デバッグメニュー</span>
        </button>
      )}

      {isOpen && (
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 w-64 overflow-hidden flex flex-col max-h-[85vh] animate-fade-in-up">
          <div className="bg-slate-800 text-white px-4 py-3 flex justify-between items-center shrink-0">
            <div className="flex items-center font-bold text-sm">
              <Bug size={16} className="mr-2" />
              Debug Menu
            </div>
            <button onClick={() => { setIsOpen(false); setIsConfirmingGenerate(false); setIsConfirmingClear(false); }} className="text-gray-400 hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>
          <div className="overflow-y-auto flex-1 custom-scrollbar">
            <div className="p-2 space-y-1">
                <div className="px-3 py-2 text-[10px] font-black text-gray-400 uppercase tracking-widest">データ操作</div>
                
                {/* 生成ボタン */}
                {!isConfirmingGenerate && !isGenerating ? (
                  <button onClick={() => { setIsConfirmingGenerate(true); setIsConfirmingClear(false); }} className={`w-full text-left rounded-lg ${linkClass} flex items-center`}>
                      <Database size={16} className="mr-2 text-blue-500" />デモデータ生成
                  </button>
                ) : isGenerating ? (
                  <div className="px-4 py-2 text-sm text-blue-600 font-bold flex items-center bg-blue-50 rounded-lg">
                      <Loader2 size={16} className="animate-spin mr-2" />
                      生成中...
                  </div>
                ) : (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100 space-y-2 mx-1">
                      <p className="text-[10px] font-bold text-blue-800 leading-tight text-center">デモデータを生成しますか？</p>
                      <div className="flex gap-1">
                          <button onClick={handleGenerateData} className="flex-1 bg-blue-600 text-white text-[10px] font-bold py-1.5 rounded">実行</button>
                          <button onClick={() => setIsConfirmingGenerate(false)} className="flex-1 bg-white border border-blue-200 text-slate-500 text-[10px] font-bold py-1.5 rounded">中止</button>
                      </div>
                  </div>
                )}

                {/* 削除ボタン */}
                {!isConfirmingClear && !isClearing ? (
                  <button onClick={() => { setIsConfirmingClear(true); setIsConfirmingGenerate(false); }} className={`w-full text-left rounded-lg ${linkClass} flex items-center text-red-600 hover:text-red-700`}>
                      <Trash2 size={16} className="mr-2" />全データ初期化
                  </button>
                ) : isClearing ? (
                  <div className="px-4 py-2 text-sm text-red-600 font-bold flex items-center bg-red-50 rounded-lg">
                      <Loader2 size={16} className="animate-spin mr-2" />
                      初期化中...
                  </div>
                ) : (
                  <div className="p-3 bg-red-50 rounded-lg border border-red-100 space-y-2 mx-1">
                      <p className="text-[10px] font-bold text-red-800 leading-tight text-center">オーナーの全データを削除して<br/>初期状態に戻しますか？</p>
                      <div className="flex gap-1">
                          <button onClick={handleClearData} className="flex-1 bg-red-600 text-white text-[10px] font-bold py-1.5 rounded">削除実行</button>
                          <button onClick={() => setIsConfirmingClear(false)} className="flex-1 bg-white border border-blue-200 text-slate-500 text-[10px] font-bold py-1.5 rounded">中止</button>
                      </div>
                  </div>
                )}

                <div className="border-t border-gray-100 mt-3 pt-3">
                    <div className="px-3 py-1 text-[10px] font-black text-gray-400 uppercase tracking-widest flex items-center">
                        <Shield size={10} className="mr-1.5" /> 店舗管理者（PC想定）
                    </div>
                    <div onClick={() => handleNavigate('/admin/shifts')} className={`rounded-lg ${linkClass}`}>シフト確認</div>
                    <div onClick={() => handleNavigate('/admin/shift-creation')} className={`rounded-lg ${linkClass}`}>シフト作成（期間指定）</div>
                    <div onClick={() => handleNavigate('/admin/staff')} className={`rounded-lg ${linkClass}`}>スタッフ管理</div>
                    <div onClick={() => handleNavigate('/admin/attendance')} className={`rounded-lg ${linkClass}`}>勤怠管理</div>
                    <div onClick={() => handleNavigate('/admin/settings')} className={`rounded-lg ${linkClass}`}>設定・プラン</div>
                    <div onClick={() => handleNavigate('/admin/setup')} className={`rounded-lg ${linkClass} text-slate-400`}>初期セットアップ</div>
                </div>

                <div className="border-t border-gray-100 mt-3 pt-3">
                    <div className="px-3 py-1 text-[10px] font-black text-blue-500 uppercase tracking-widest flex items-center">
                        <Smartphone size={10} className="mr-1.5" /> スタッフ側（一般ユーザー）
                    </div>
                    <div onClick={() => handleNavigate('/user/shifts')} className={`rounded-lg ${linkClass}`}>シフト希望提出</div>
                    <div onClick={() => handleNavigate('/user/attendance')} className={`rounded-lg ${linkClass}`}>勤怠打刻</div>
                    <div onClick={() => handleNavigate('/user/settings')} className={`rounded-lg ${linkClass}`}>デフォルト希望設定</div>
                    <div onClick={() => handleNavigate('/invite')} className={`rounded-lg ${linkClass} flex items-center text-emerald-600`}>
                        <UserPlus size={14} className="mr-2" />招待・LINE連携画面
                    </div>
                </div>

                <div className="border-t border-gray-100 mt-3 pt-2">
                    <div onClick={() => handleNavigate('/login')} className={`rounded-lg ${linkClass} text-slate-400 font-normal`}>ログアウト・ログイン画面</div>
                </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DebugMenu;